import React, { useMemo, useState } from 'react';
import { Check, Heart, Star, ThumbsUp, X } from 'lucide-react';
import { barajar } from '../../../lib/formularios';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { SinRespuesta } from './CamposTexto';

/*
 * Escala lineal, calificación (estrellas, corazones, pulgares) y las dos cuadrículas.
 */

export function CampoEscala({ item, valor, onCambio, soloLectura, aria }) {
  const { min = 1, max = 5, etiqueta_min: etiquetaMin = '', etiqueta_max: etiquetaMax = '' } = item.escala || {};
  const valores = [];
  for (let v = min; v <= max; v += 1) valores.push(v);
  const grupo = `fr-esc-${item.id}`;

  return (
    <div className="fr-escala-caja">
      <div className="fr-escala" role="radiogroup" {...aria}>
        {etiquetaMin && <span className="fr-escala-etiqueta is-min">{etiquetaMin}</span>}
        <div className="fr-escala-valores">
          {valores.map((v) => (
            <label key={v} className={`fr-escala-valor ${valor === v ? 'is-elegida' : ''}`}>
              <span className="fr-escala-numero">{v}</span>
              <input
                type="radio"
                name={grupo}
                checked={valor === v}
                disabled={soloLectura}
                onChange={() => onCambio(v)}
                aria-label={`${v}${v === min && etiquetaMin ? ` (${etiquetaMin})` : ''}${v === max && etiquetaMax ? ` (${etiquetaMax})` : ''}`}
              />
            </label>
          ))}
        </div>
        {etiquetaMax && <span className="fr-escala-etiqueta is-max">{etiquetaMax}</span>}
      </div>
      {soloLectura && (valor === null || valor === undefined) && <SinRespuesta />}
      {!soloLectura && !item.obligatoria && valor !== null && valor !== undefined && (
        <div className="fr-borrar-seleccion">
          <button type="button" className="fm-boton-texto" onClick={() => onCambio(null)}>Borrar selección</button>
        </div>
      )}
    </div>
  );
}

const ICONOS = { estrella: Star, corazon: Heart, pulgar: ThumbsUp };

export function CampoCalificacion({ item, valor, onCambio, soloLectura, aria }) {
  const max = item.calificacion?.max || 5;
  const Icono = ICONOS[item.calificacion?.icono] || Star;
  const [encima, setEncima] = useState(null);
  const mostrado = encima ?? valor ?? 0;

  // Teclado como un grupo de radios: flechas mueven, Inicio/Fin van a los extremos
  const alTeclado = (e) => {
    if (soloLectura) return;
    const actual = valor || 0;
    let nuevo = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') nuevo = Math.min(max, actual + 1);
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') nuevo = Math.max(1, actual - 1);
    if (e.key === 'Home') nuevo = 1;
    if (e.key === 'End') nuevo = max;
    if (nuevo !== null) {
      e.preventDefault();
      onCambio(nuevo);
      const destino = e.currentTarget.querySelectorAll('button')[nuevo - 1];
      destino?.focus();
    }
  };

  return (
    <div className="fr-calificacion-caja">
      <div
        className="fr-calificacion"
        role="radiogroup"
        onKeyDown={alTeclado}
        onMouseLeave={() => setEncima(null)}
        {...aria}
      >
        {Array.from({ length: max }, (_, i) => i + 1).map((v) => {
          const lleno = v <= mostrado;
          return (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={valor === v}
              aria-label={`${v} de ${max}`}
              tabIndex={soloLectura ? -1 : (valor ? (valor === v ? 0 : -1) : (v === 1 ? 0 : -1))}
              disabled={soloLectura}
              className={`fr-calificacion-boton ${lleno ? 'is-llena' : ''}`}
              onMouseEnter={() => !soloLectura && setEncima(v)}
              onClick={() => onCambio(v)}
            >
              <span className="fr-calificacion-numero">{v}</span>
              <Icono className="fr-calificacion-icono" fill={lleno ? 'currentColor' : 'none'} />
            </button>
          );
        })}
      </div>
      {soloLectura && !valor && <SinRespuesta />}
      {!soloLectura && !item.obligatoria && valor ? (
        <div className="fr-borrar-seleccion">
          <button type="button" className="fm-boton-texto" onClick={() => onCambio(null)}>Borrar selección</button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Cuadrícula de opción múltiple y de casillas. En escritorio, tabla; en el teléfono una tabla
 * de 5 columnas no cabe, así que cada fila se apila con sus opciones debajo.
 * `marcas.correctas` = { fila_id: [col_ids] } (revisión de un cuestionario).
 */
export function CampoCuadricula({ item, valor, onCambio, soloLectura, semilla, marcas, aria }) {
  const casillas = item.tipo === 'cuadricula_casillas';
  const movil = useIsMobile(640);
  const filas = useMemo(
    () => (item.mezclar_filas && semilla ? barajar(item.filas || [], `${semilla}:${item.id}`) : (item.filas || [])),
    [item.filas, item.mezclar_filas, item.id, semilla],
  );
  const columnas = item.columnas || [];
  const actual = valor || {};

  const elegido = (filaId, colId) => {
    const v = actual[filaId];
    return casillas ? (v || []).includes(colId) : v === colId;
  };

  const cambiar = (filaId, colId) => {
    const n = { ...actual };
    if (casillas) {
      const lista = n[filaId] || [];
      const nueva = lista.includes(colId) ? lista.filter((c) => c !== colId) : [...lista, colId];
      if (nueva.length) n[filaId] = columnas.map((c) => c.id).filter((c) => nueva.includes(c));
      else delete n[filaId];
    } else {
      n[filaId] = colId;
    }
    onCambio(Object.keys(n).length ? n : null);
  };

  const marcaCelda = (filaId, colId) => {
    const correctas = marcas?.correctas;
    if (!soloLectura || !correctas || typeof correctas !== 'object') return '';
    const buena = (correctas[filaId] || []).includes(colId);
    const sel = elegido(filaId, colId);
    if (sel && buena) return 'is-correcta';
    if (sel && !buena) return 'is-incorrecta';
    if (!sel && buena) return 'is-correcta-perdida';
    return '';
  };

  const control = (fila, col, etiqueta) => {
    const marca = marcaCelda(fila.id, col.id);
    return (
      <span className={`fr-celda-control ${marca}`}>
        <input
          type={casillas ? 'checkbox' : 'radio'}
          name={`fr-cua-${item.id}-${fila.id}`}
          checked={elegido(fila.id, col.id)}
          disabled={soloLectura}
          onChange={() => cambiar(fila.id, col.id)}
          aria-label={etiqueta}
        />
        {marca === 'is-correcta' && <Check className="fr-marca-icono is-bien" aria-label="Correcta" />}
        {marca === 'is-incorrecta' && <X className="fr-marca-icono is-mal" aria-label="Incorrecta" />}
      </span>
    );
  };

  const limpiarFilas = !soloLectura && !item.obligatoria && Object.keys(actual).length > 0;

  if (movil) {
    return (
      <div className="fr-cuadricula-apilada" role="group" {...aria}>
        {filas.map((fila) => (
          <fieldset key={fila.id} className="fr-cuadricula-fila-movil">
            <legend className="fr-cuadricula-fila-titulo">{fila.texto}</legend>
            {columnas.map((col) => (
              <label key={col.id} className="fr-opcion">
                <span className="fr-opcion-fila">
                  {control(fila, col, `${fila.texto}: ${col.texto}`)}
                  <span className="fr-opcion-texto">{col.texto}</span>
                </span>
              </label>
            ))}
          </fieldset>
        ))}
        {limpiarFilas && (
          <div className="fr-borrar-seleccion">
            <button type="button" className="fm-boton-texto" onClick={() => onCambio(null)}>Borrar selecciones</button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="fr-cuadricula-caja">
      <div className="fr-cuadricula-scroll fm-scroll">
        <table className="fr-cuadricula" {...aria}>
          <thead>
            <tr>
              <th scope="col"><span className="sr-only">Fila</span></th>
              {columnas.map((col) => <th key={col.id} scope="col">{col.texto}</th>)}
            </tr>
          </thead>
          <tbody>
            {filas.map((fila) => (
              <tr key={fila.id}>
                <th scope="row">{fila.texto}</th>
                {columnas.map((col) => (
                  <td key={col.id}>
                    <label className="fr-celda">{control(fila, col, `${fila.texto}: ${col.texto}`)}</label>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {limpiarFilas && (
        <div className="fr-borrar-seleccion">
          <button type="button" className="fm-boton-texto" onClick={() => onCambio(null)}>Borrar selecciones</button>
        </div>
      )}
    </div>
  );
}
